import { useEffect, useState } from 'react'
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google'
import { useQueryClient } from '@tanstack/react-query'
import { API_BASE, setSessionExpiredHandler } from '../../lib/api'
import { useAuthStore } from '../../stores/authStore'

// Session handling for BOSS MultiPO (docs/BossMultiPO.md §5, decision 10). Sessions keep the
// site's fixed lifetime. A banner warns before the end; once the session is over, an overlay asks
// for a fresh sign-in on top of the grid, which stays as it was, and unsaved cells are retried.

/** Minutes before the end of a session when the warning shows. */
export const WARN_MINUTES = 5

/** When the access token expires (ms since the epoch), or null when it can't be read. */
export function tokenExpiry(token: string | null): number | null {
    if (!token) return null
    try {
        const payload = token.split('.')[1]
        if (!payload) return null
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=')
        const exp: unknown = JSON.parse(atob(base64))?.exp
        return typeof exp === 'number' ? exp * 1000 : null
    } catch {
        return null
    }
}

export type SessionPhase = 'ok' | 'ending' | 'expired'

export function sessionPhase(expiresAt: number | null, now: number, refused: boolean): SessionPhase {
    if (refused || (expiresAt !== null && expiresAt <= now)) return 'expired'
    if (expiresAt !== null && expiresAt - now <= WARN_MINUTES * 60_000) return 'ending'
    return 'ok'
}

/** Re-reads the session another window may have signed in to; true when that session is still live. */
async function pickUpStoredSession(): Promise<boolean> {
    await useAuthStore.persist.rehydrate()
    const expiresAt = tokenExpiry(useAuthStore.getState().accessToken)
    return expiresAt !== null && expiresAt > Date.now()
}

export default function SessionGuard() {
    const queryClient = useQueryClient()
    const token = useAuthStore(s => s.accessToken)
    const userId = useAuthStore(s => s.user?.id)
    const setAuth = useAuthStore(s => s.setAuth)
    const [now, setNow] = useState(() => Date.now())
    const [refusedToken, setRefusedToken] = useState<string | null>(null)
    const [signingIn, setSigningIn] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 15_000)
        return () => window.clearInterval(id)
    }, [])

    // A 401 here keeps the page: unless another window has already signed in again, show the overlay.
    useEffect(() => setSessionExpiredHandler(() => {
        const refused = useAuthStore.getState().accessToken
        void pickUpStoredSession().then(live => { if (!live) setRefusedToken(refused) })
    }), [])

    // A sign-in in another window lands in localStorage; this window picks it up in place.
    useEffect(() => {
        const onStorage = (e: StorageEvent) => { if (e.key === 'puresoft-auth') void pickUpStoredSession() }
        window.addEventListener('storage', onStorage)
        return () => window.removeEventListener('storage', onStorage)
    }, [])

    // A new token clears the refusal and reloads whatever failed while the session was out.
    useEffect(() => {
        if (!token || token === refusedToken) return
        setRefusedToken(null)
        setSigningIn(false)
        void queryClient.refetchQueries({ type: 'active', predicate: q => q.state.status === 'error' })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    const expiresAt = tokenExpiry(token)
    const phase = sessionPhase(expiresAt, now, refusedToken !== null && refusedToken === token)

    const onGoogle = async (response: CredentialResponse) => {
        if (!response.credential) return setError('No credential received from Google.')
        setBusy(true)
        setError(null)
        try {
            const res = await fetch(API_BASE + '/api/auth/google', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken: response.credential }),
            })
            if (res.status === 401) return setError('Access denied. Only @vpo.ca accounts are allowed.')
            if (res.status === 403) return setError('Your account has been deactivated. Contact an administrator.')
            if (!res.ok) return setError('Sign-in failed. Please try again.')
            const data = await res.json()
            if (data.user?.id !== userId) {
                // Someone else signed in: nothing on screen is theirs to keep.
                queryClient.clear()
                setAuth(data.user, data.accessToken, data.refreshToken)
                window.location.reload()
                return
            }
            setAuth(data.user, data.accessToken, data.refreshToken)
        } catch {
            setError('Network error. Check your connection and try again.')
        } finally {
            setBusy(false)
        }
    }

    const until = expiresAt ? new Date(expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''
    const showOverlay = phase === 'expired' || signingIn

    return (
        <>
            {phase === 'ending' && !signingIn && (
                <div className="multipo-session-banner" role="status">
                    Your session ends at {until}. Sign in again now to keep working without interruption.
                    <button className="btn btn-secondary multipo-btn-sm" onClick={() => setSigningIn(true)}>Sign in again</button>
                </div>
            )}
            {showOverlay && (
                <div className="modal-backdrop">
                    <div className="modal" style={{ maxWidth: 420 }}>
                        <div className="modal-header">
                            <h2>{phase === 'expired' ? 'Your session has ended' : 'Sign in again'}</h2>
                            {phase !== 'expired' && <button className="modal-close" onClick={() => setSigningIn(false)}>✕</button>}
                        </div>
                        <div className="modal-body">
                            <p style={{ marginTop: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                                {phase === 'expired'
                                    ? 'Sign in to carry on. Your grid stays as it is, and any quantity that was not saved is saved once you are back.'
                                    : 'Signing in now starts a fresh session. Your grid stays as it is.'}
                            </p>
                            <div className="multipo-session-google">
                                <GoogleLogin onSuccess={onGoogle} onError={() => setError('Google sign-in was cancelled or failed.')}
                                    size="large" text="signin_with" shape="pill" />
                            </div>
                            {busy && <p style={{ color: 'var(--color-text-secondary)' }}>Signing in…</p>}
                            {error && <p className="multipo-session-error">{error}</p>}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
